/* 레지스트리 버킷 S3 클라이언트 — 앱 설치파일(app/)·펌웨어(firmware/) 버전 관리 전용.
   s3.js(코드 예제 버킷, 동결 인터페이스) 의 미러. 차이점 둘:
     · 대상 버킷 = REGISTRY_BUCKET (CODE_BUCKET 아님). 기존 s3.js 호출처 무회귀.
     · listObjects 가 Size/LastModified 를 보존 — s3.js listPrefix(키만)와 달리 버전 관리
       UI 가 용량·갱신시각을 표시해야 하므로.
   fake 모드(env 미설정)는 별도 localStorage 버킷('adm.fake.registry')으로 동작 —
   코드 예제 fake 버킷과 독립. manifest/installer 는 public-read(앱이 익명 다운로드)라
   읽기는 익명 GET, 쓰기/목록만 developer STS 자격(SigV4) 사용. */
import { isFakeMode, REGISTRY_BUCKET, REGISTRY_BUCKET_URL, AWS_REGION } from '../config/env.js';
import {
  S3Client,
  PutObjectCommand,
  DeleteObjectCommand,
  ListObjectsV2Command,
} from '@aws-sdk/client-s3';

const FAKE_KEY = 'adm.fake.registry';   // { "<key>": { body, contentType, size, lastModified } }
const delay = (ms = 160) => new Promise(r => setTimeout(r, ms));

function loadBucket() {
  try { return JSON.parse(localStorage.getItem(FAKE_KEY) || '{}'); } catch (e) { return {}; }
}
function saveBucket(b) { try { localStorage.setItem(FAKE_KEY, JSON.stringify(b)); } catch (e) { /* ignore */ } }

// 버킷 URL → 객체 key 추출 (fake 매핑). 절대/상대 URL 모두 허용.
function keyFromUrl(url) {
  try {
    if (REGISTRY_BUCKET_URL && url.startsWith(REGISTRY_BUCKET_URL)) return url.slice(REGISTRY_BUCKET_URL.length).replace(/^\//, '');
    return new URL(url).pathname.replace(/^\//, '');
  } catch (e) {
    return String(url).replace(/^\//, '');
  }
}

// 직렬화 — fake 는 localStorage 라 문자열만. 바이너리는 마커로 대체.
function serialize(body) {
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object' && !ArrayBuffer.isView(body) && !(body instanceof ArrayBuffer) && !(typeof Blob !== 'undefined' && body instanceof Blob)) {
    try { return JSON.stringify(body); } catch (e) { return '[object]'; }
  }
  return '[binary]';
}

/* ---------- real(live) 헬퍼 ---------- */
async function clientFor(getCredentials) {
  const creds = typeof getCredentials === 'function' ? await getCredentials() : getCredentials;
  if (!creds || !creds.accessKeyId) {
    const err = new Error('로그인이 필요합니다 — S3 자격증명을 가져오지 못했습니다.');
    err.status = 401;
    throw err;
  }
  return new S3Client({
    region: creds.region || AWS_REGION,
    credentials: {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
    },
  });
}

function toBody(body) {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return body;
  if (body instanceof ArrayBuffer) return new Uint8Array(body);
  if (ArrayBuffer.isView(body)) return body;
  return JSON.stringify(body);
}

/* ---------- API ---------- */
/* 익명 GET(JSON) — manifest 는 public-read. 404/403 → status 부착 throw(호출부가 단락). */
export async function getJsonAnon(url) {
  if (isFakeMode) {
    await delay();
    const bucket = loadBucket();
    const entry = bucket[keyFromUrl(url)];
    if (!entry) { const err = new Error('Not Found'); err.status = 404; throw err; }
    return JSON.parse(entry.body);
  }
  const res = await fetch(url, { cache: 'no-store' });
  if (res.status === 404 || res.status === 403) {
    const err = new Error('Not Found'); err.status = res.status; throw err;
  }
  if (!res.ok) { const err = new Error(`GET 실패: ${res.status}`); err.status = res.status; throw err; }
  return res.json();
}

/* 익명 GET(텍스트) — .sha256 사이드카 등. 없음/실패 → null(throw 안 함, 선택적 보강용). */
export async function getTextAnon(url) {
  if (isFakeMode) {
    await delay();
    const bucket = loadBucket();
    const entry = bucket[keyFromUrl(url)];
    return entry ? entry.body : null;
  }
  let res;
  try { res = await fetch(url, { cache: 'no-store' }); } catch (e) { return null; }
  if (!res.ok) return null;
  return res.text();
}

/* opts.cacheControl = S3 Cache-Control 헤더(예: 'max-age=300'). 미지정 시 종전과 동일. */
export async function putObject(getCredentials, key, body, contentType, opts = {}) {
  if (isFakeMode) {
    await delay();
    const bucket = loadBucket();
    const s = serialize(body);
    bucket[key] = { body: s, contentType: contentType || 'application/octet-stream', size: s.length, lastModified: new Date().toISOString() };
    saveBucket(bucket);
    return;
  }
  const client = await clientFor(getCredentials);
  await client.send(new PutObjectCommand({
    Bucket: REGISTRY_BUCKET,
    Key: key,
    Body: toBody(body),
    ContentType: contentType || 'application/octet-stream',
    ...(opts.cacheControl ? { CacheControl: opts.cacheControl } : {}),
  }));
}

export async function deleteObject(getCredentials, key) {
  if (isFakeMode) {
    await delay();
    const bucket = loadBucket();
    delete bucket[key];
    saveBucket(bucket);
    return;
  }
  const client = await clientFor(getCredentials);
  await client.send(new DeleteObjectCommand({ Bucket: REGISTRY_BUCKET, Key: key }));
}

/* 목록(키·Size·LastModified) — ListObjectsV2(s3:ListBucket 필요 → IAM 미적용 시 403).
   s3.js listPrefix 와 달리 메타데이터 보존. LastModified 는 ISO 문자열로 정규화. */
export async function listObjects(getCredentials, prefix) {
  if (isFakeMode) {
    await delay();
    const bucket = loadBucket();
    return Object.keys(bucket).filter(k => k.startsWith(prefix)).map(k => ({
      key: k,
      size: bucket[k].size != null ? bucket[k].size : (bucket[k].body ? bucket[k].body.length : 0),
      lastModified: bucket[k].lastModified || null,
    }));
  }
  const client = await clientFor(getCredentials);
  const out = [];
  let token;
  do {
    const r = await client.send(new ListObjectsV2Command({
      Bucket: REGISTRY_BUCKET, Prefix: prefix, ContinuationToken: token,
    }));
    (r.Contents || []).forEach(o => {
      if (o.Key) out.push({ key: o.Key, size: o.Size || 0, lastModified: o.LastModified ? new Date(o.LastModified).toISOString() : null });
    });
    token = r.IsTruncated ? r.NextContinuationToken : undefined;
  } while (token);
  return out;
}

/* fake 모드 전용 시드 — 명시 size/lastModified 보존(데모 데이터 현실감). live 는 no-op. */
export function seedFakeObjects(entries) {
  if (!isFakeMode) return;
  const bucket = loadBucket();
  for (const e of entries) {
    const body = typeof e.body === 'string' ? e.body : serialize(e.body);
    bucket[e.key] = {
      body,
      contentType: e.contentType || 'application/octet-stream',
      size: e.size != null ? e.size : body.length,
      lastModified: e.lastModified || new Date().toISOString(),
    };
  }
  saveBucket(bucket);
}
